import { useEffect, useState } from 'react'

/**
 * Dependency-free SPA navigation. The app's routing is pathname-based
 * (RootRouter in App.tsx decides AppRoutes vs LandingPage vs AuthCallback)
 * but pre-v0.8.24 every transition was a `window.location.assign / replace`
 * — a full page reload that bounces through AuthProvider hydration, re-runs
 * all initial useEffects, and was the load-bearing source of the founder's
 * "loading is choppy" report.
 *
 * `softNavigate` updates the pathname via `history.pushState` (so back-button
 * still works) and dispatches a custom event that components subscribed via
 * `useCurrentPathname` listen to. `popstate` (back/forward) is also handled.
 *
 * Use ONLY for routes RootRouter knows about: `/`, `/landing`,
 * `/auth/callback`. Other paths fall through to a default render — the
 * router doesn't currently 404. Hard navigations (`window.location.assign`)
 * remain appropriate when a real reload is the intent (e.g. the
 * LoadingPlaceholder escape hatch where we WANT to re-attempt auth
 * hydration from scratch).
 */
const ROUTE_CHANGE_EVENT = 'debtos:routechange'

export function softNavigate(pathname: string): void {
  if (typeof window === 'undefined') return
  if (window.location.pathname === pathname) return
  window.history.pushState({}, '', pathname)
  window.dispatchEvent(new CustomEvent(ROUTE_CHANGE_EVENT))
}

/**
 * Subscribe to pathname changes. Re-renders the calling component when the
 * URL changes — either via `softNavigate`, the browser back/forward
 * buttons, or any other code path that dispatches the custom event.
 *
 * Returns the current `window.location.pathname` so components can decide
 * what to render. SSR-safe: returns `'/'` when `window` is undefined.
 */
export function useCurrentPathname(): string {
  const [pathname, setPathname] = useState(() =>
    typeof window === 'undefined' ? '/' : window.location.pathname
  )

  useEffect(() => {
    if (typeof window === 'undefined') return
    const handler = () => setPathname(window.location.pathname)
    window.addEventListener('popstate', handler)
    window.addEventListener(ROUTE_CHANGE_EVENT, handler)
    return () => {
      window.removeEventListener('popstate', handler)
      window.removeEventListener(ROUTE_CHANGE_EVENT, handler)
    }
  }, [])

  return pathname
}
